import { memo, useMemo } from "react";

import Icon from "@/components/Icon/Icon";
import { Text } from "@/components/Typography/Text";
import { Button } from "@/components/ui/button";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { cn } from "@/lib/cn";

import { useForm } from "../../hooks/useForm";
import { useFormStoreContext } from "../../store/zustand-context";
import {
  type AttributeCollectionId,
  type AttributeGroupId,
  type Attribute as AttributeType,
  type FieldId,
  FieldType,
} from "../../types";
import { RemoveButton } from "../collections/RemoveButton";
import { FormField } from "../fields/FormField";

import { useGroupOperations } from "./useGroupOperations";

/**
 * 属性グループ型定義
 */
interface AttributeGroupProps {
  /** グループID */
  groupId: AttributeGroupId;

  /** グループインデックス */
  groupIndex?: number;

  /** コレクションID */
  collectionId?: AttributeCollectionId;

  /** コレクションインデックス */
  collectionIndex?: number;

  /** 読み取り専用モード */
  readOnly?: boolean;

  layout?: "vertical" | "horizontal";

  className?: string;

  "data-testid"?: string;
}

/**
 * 属性グループコンポーネント
 * グループに属する属性フィールドのレンダリングを担当
 */
export const AttributeGroup = memo(
  ({
    groupId,
    groupIndex = 0,
    collectionId,
    collectionIndex = 0,
    readOnly,
    layout = "vertical",
    className,
    "data-testid": testId,
  }: AttributeGroupProps) => {
    const tc = useCommonTranslation();
    const store = useFormStoreContext();
    const form = useForm();

    const {
      groupData,
      showRemoveButton,
      showAddButton,
      removeGroup,
      addGroup,
    } = useGroupOperations({
      collectionId,
      groupId,
      groupIndex,
      store,
    });

    // 属性一覧の取得と表示順のソート
    const attributes = useMemo(() => {
      if (!groupData?.attributes) {
        return [];
      }

      return [...groupData.attributes].sort(
        (a, b) => (a.itemOrder || 0) - (b.itemOrder || 0),
      );
    }, [groupData]);

    // フィールドIDのプレフィックス
    const fieldIdPrefix = useMemo(() => {
      // フォーマット: collectionId.collectionIndex.groupId.groupIndex
      return collectionId
        ? `${collectionId}.${collectionIndex}.${groupId}.${groupIndex}`
        : `default-collection.0.${groupId}.${groupIndex}`;
    }, [groupId, groupIndex, collectionId, collectionIndex]);

    // 全て非表示の場合は何も表示しない
    if (
      !groupData ||
      attributes.every(
        (attribute) =>
          attribute.type === FieldType.Hidden || attribute.type === "QrCode",
      )
    ) {
      return null;
    }

    // フィールドの削除ボタンを表示するかどうかを判定
    const shouldShowFieldRemoveButton = (_fieldId: FieldId) => {
      return false;
    };

    return (
      <div
        className={cn(
          "attribute-group my-2",
          "transition-opacity duration-200",
          className,
        )}
        data-testid={testId}
        data-group-id={groupId}
      >
        <div
          className={cn(
            groupData.isDynamic && "border-s ps-4 border-neutral-muted-border",
          )}
        >
          <div className="flex items-center justify-between">
            {groupData.name && (
              <Text variant="label" className="text-neutral-muted-on-fill">
                {groupData.name}
              </Text>
            )}
            {showRemoveButton && !readOnly && (
              <RemoveButton
                index={groupIndex}
                onRemove={(idx) => removeGroup(idx)}
                type="group"
              />
            )}
          </div>

          {groupData.description && (
            <Text
              variant="labelSmallReg"
              className="text-neutral-muted-on-fill"
            >
              {groupData.description}
            </Text>
          )}

          {/* 属性フィールドのグリッド表示 */}
          {attributes.map((attribute: AttributeType) => {
            const attributeId = attribute.id;
            const fieldId = `${fieldIdPrefix}.${attributeId}` as FieldId;

            return (
              <div key={fieldId} className="relative my-5">
                {shouldShowFieldRemoveButton(fieldId) && (
                  <RemoveButton
                    index={groupIndex}
                    onRemove={() => {
                      if (collectionId && form.getDynamicFields) {
                        const fieldActions = form.getDynamicFields(
                          collectionId,
                          groupId,
                          attributeId,
                        );
                        if (fieldActions && fieldActions.removeField) {
                          fieldActions.removeField(fieldId);
                        }
                      }
                    }}
                    type="field"
                  />
                )}
                <FormField
                  fieldId={fieldId}
                  attribute={attribute}
                  readOnly={readOnly}
                  layout={layout}
                  allValues={store.values}
                  data-testid={`field-${attributeId}`}
                />
              </div>
            );
          })}
        </div>

        {showAddButton && !readOnly && (
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={addGroup}
              className="transition-transform active:scale-95"
              data-testid="add-dynamic-group-button"
            >
              <Icon iconName="PlusIcon" className="mr-2" />
              {tc("action.add")}
            </Button>
          </div>
        )}
      </div>
    );
  },
);

AttributeGroup.displayName = "AttributeGroup";
